import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, CircleHelp, Search, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { Button } from "./Button";
import { Dialog } from "./Dialog";

/**
 * Botão de ajuda contextual da barra superior. Não sabe de onde vem o conteúdo: recebe `load(route)`
 * (tópicos da tela, dicas de campo, tour, FAQ e glossário) e, opcionalmente, `search(q)`.
 * O módulo `help` liga isto ao banco (help.get_bundle, help.search, help.record_feedback).
 */

export interface HelpTopic {
  id: string;
  key: string;
  title: string;
  summary?: string | null;
  body_md: string;
  module?: string;
  version?: number;
}
export interface HelpHint {
  entity: string;
  field: string;
  short_tip: string;
  error_hint?: string | null;
}
export interface HelpTourStep {
  anchor?: string;
  title: string;
  body: string;
}
export interface HelpTour {
  key: string;
  title: string;
  steps: HelpTourStep[];
}
export interface HelpBundle {
  topics: HelpTopic[];
  hints: HelpHint[];
  faq: Array<{ id: string; question: string; answer_md: string }>;
  glossary: Array<{ term: string; definition_md: string; synonyms?: string[] }>;
  tour: HelpTour | null;
}
export interface HelpSearchResult {
  kind: "topic" | "faq" | "glossary";
  ref: string;
  title: string;
  snippet: string | null;
  route?: string | null;
}

export interface HelpButtonProps {
  /** Rota atual (ex.: location.pathname). Mudar a rota recarrega a ajuda. */
  route: string;
  load: (route: string) => Promise<HelpBundle>;
  search?: (q: string) => Promise<HelpSearchResult[]>;
  onFeedback?: (topic: HelpTopic, helpful: boolean) => Promise<unknown> | unknown;
  /** Link "Abrir a central de ajuda". */
  onOpenCenter?: () => void;
  /** Atraso da busca, em ms. */
  debounceMs?: number;
}

// ---------------------------------------------------------------- markdown mínimo e seguro

function inline(text: string, keyBase: string): ReactNode[] {
  // **negrito**, `código`; todo o resto é texto (nunca HTML).
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    out.push(tok.startsWith("**") ? <strong key={`${keyBase}-${i++}`}>{tok.slice(2, -2)}</strong> : <code key={`${keyBase}-${i++}`}>{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Markdown de ajuda: títulos (#), listas (- e 1.), parágrafos, **negrito** e `código`. Sem HTML. */
export function HelpMarkdown({ source }: { source: string }) {
  const blocks: ReactNode[] = [];
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push(<p key={k++} className="vx-help__h">{inline(h[2]!, `h${k}`)}</p>);
      i++;
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^\s*([-*]|\d+\.)\s+/, ""));
        i++;
      }
      const List = ordered ? "ol" : "ul";
      blocks.push(
        <List key={k++} className="vx-help__list">
          {items.map((t, j) => (
            <li key={j}>{inline(t, `l${k}-${j}`)}</li>
          ))}
        </List>,
      );
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,3}\s|\s*([-*]|\d+\.)\s+)/.test(lines[i]!)) {
      para.push(lines[i]!.trim());
      i++;
    }
    blocks.push(<p key={k++}>{inline(para.join(" "), `p${k}`)}</p>);
  }
  return <div className="vx-help__md">{blocks}</div>;
}

// ---------------------------------------------------------------- tour guiado

export function HelpTourOverlay({ tour, onClose }: { tour: HelpTour; onClose: () => void }) {
  const [step, setStep] = useState(0);
  const s = tour.steps[step];
  useEffect(() => {
    if (!s?.anchor) return;
    const el = document.querySelector<HTMLElement>(`[data-tour="${s.anchor.replace(/[^a-z0-9_-]/gi, "")}"]`);
    if (!el) return;
    el.classList.add("vx-tour-target");
    el.scrollIntoView?.({ block: "center", behavior: "smooth" });
    return () => el.classList.remove("vx-tour-target");
  }, [s?.anchor]);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!s) return null;
  const lastStep = step === tour.steps.length - 1;
  return (
    <div className="vx-tour" role="dialog" aria-modal="false" aria-labelledby="vx-tour-title">
      <div className="vx-tour__head">
        <span className="vx-tour__count mono">
          {step + 1}/{tour.steps.length}
        </span>
        <strong id="vx-tour-title">{s.title}</strong>
        <span className="vx-spacer" />
        <Button variant="ghost" size="sm" iconOnly label="Encerrar o tour" icon={<X />} onClick={onClose} />
      </div>
      <p className="vx-tour__body">{s.body}</p>
      <div className="vx-tour__foot">
        <Button size="sm" icon={<ArrowLeft />} disabled={step === 0} onClick={() => setStep((n) => n - 1)}>
          Anterior
        </Button>
        {lastStep ? (
          <Button size="sm" variant="primary" onClick={onClose}>
            Concluir
          </Button>
        ) : (
          <Button size="sm" variant="primary" icon={<ArrowRight />} onClick={() => setStep((n) => n + 1)}>
            Próximo
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- botão e painel

const KIND_LABEL: Record<HelpSearchResult["kind"], string> = { topic: "Tópico", faq: "Pergunta", glossary: "Glossário" };

export function HelpButton({ route, load, search, onFeedback, onOpenCenter, debounceMs = 250 }: HelpButtonProps) {
  const [open, setOpen] = useState(false);
  const [bundle, setBundle] = useState<HelpBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<HelpSearchResult[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [voted, setVoted] = useState<Record<string, boolean>>({});
  const [tour, setTour] = useState<HelpTour | null>(null);
  const loadedFor = useRef<string | null>(null);

  const fetchBundle = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const b = await load(route);
      setBundle(b);
      setExpanded(b.topics[0]?.id ?? null);
      loadedFor.current = route;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar a ajuda");
    } finally {
      setLoading(false);
    }
  }, [load, route]);

  useEffect(() => {
    if (open && loadedFor.current !== route) void fetchBundle();
  }, [open, route, fetchBundle]);

  useEffect(() => {
    if (!search || q.trim().length < 2) {
      setResults(null);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      search(q.trim())
        .then((r) => alive && setResults(r))
        .catch(() => alive && setResults([]));
    }, debounceMs);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, search, debounceMs]);

  async function vote(topic: HelpTopic, helpful: boolean) {
    setVoted((v) => ({ ...v, [topic.id]: helpful }));
    await onFeedback?.(topic, helpful);
  }

  const empty = bundle && !bundle.topics.length && !bundle.faq.length && !bundle.hints.length && !bundle.tour;

  return (
    <>
      <Button variant="ghost" iconOnly label="Ajuda desta tela" icon={<CircleHelp />} onClick={() => setOpen(true)} data-help-button="" />
      <Dialog open={open} onOpenChange={setOpen} sheet title="Ajuda" description="O que existe para esta tela, dicas, tour e perguntas frequentes.">
        <div className="vx-stack vx-help">
          {search ? (
            <div className="vx-input-group">
              <span className="vx-input-group__addon" aria-hidden="true">
                <Search />
              </span>
              <input
                className="vx-input"
                type="search"
                aria-label="Buscar na ajuda"
                placeholder="Buscar na ajuda"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          ) : null}

          {results ? (
            <section aria-label="Resultados da busca">
              {results.length === 0 ? (
                <p className="text-2">Nada encontrado para “{q.trim()}”.</p>
              ) : (
                <ul className="vx-help__results">
                  {results.map((r) => (
                    <li key={`${r.kind}-${r.ref}`}>
                      <span className="vx-pill vx-pill--plain">{KIND_LABEL[r.kind]}</span> <strong>{r.title}</strong>
                      {r.snippet ? <p className="text-2">{r.snippet}</p> : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : loading && !bundle ? (
            <p className="text-2" role="status">
              Carregando a ajuda...
            </p>
          ) : error ? (
            <div className="vx-callout vx-callout--warn" role="alert">
              {error}{" "}
              <Button size="sm" variant="link" onClick={() => void fetchBundle()}>
                Tentar de novo
              </Button>
            </div>
          ) : bundle ? (
            <>
              {empty ? <p className="text-2">Ainda não há ajuda escrita para esta tela. Use a busca ou a central.</p> : null}

              {bundle.tour ? (
                <div className="vx-help__tour">
                  <div>
                    <strong>{bundle.tour.title}</strong>
                    <div className="text-2">Tour guiado em {bundle.tour.steps.length} passos.</div>
                  </div>
                  <span className="vx-spacer" />
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      setTour(bundle.tour);
                      setOpen(false);
                    }}
                  >
                    Começar o tour
                  </Button>
                </div>
              ) : null}

              {bundle.topics.map((t) => {
                const isOpen = expanded === t.id;
                return (
                  <article key={t.id} className="vx-help__topic">
                    <button type="button" className="vx-help__topic-head" aria-expanded={isOpen} onClick={() => setExpanded(isOpen ? null : t.id)}>
                      <span className="vx-help__topic-title">{t.title}</span>
                      {t.summary ? <span className="text-2">{t.summary}</span> : null}
                    </button>
                    {isOpen ? (
                      <div className="vx-help__topic-body">
                        <HelpMarkdown source={t.body_md} />
                        {onFeedback ? (
                          <div className="vx-help__vote" aria-live="polite">
                            {t.id in voted ? (
                              <span className="text-2">Obrigado pela opinião.</span>
                            ) : (
                              <>
                                <span className="text-2">Ajudou?</span>
                                <Button size="sm" variant="ghost" icon={<ThumbsUp />} onClick={() => void vote(t, true)}>
                                  Sim
                                </Button>
                                <Button size="sm" variant="ghost" icon={<ThumbsDown />} onClick={() => void vote(t, false)}>
                                  Não
                                </Button>
                              </>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </article>
                );
              })}

              {bundle.hints.length ? (
                <section aria-labelledby="vx-help-hints">
                  <h3 id="vx-help-hints" className="vx-help__section">
                    Dicas dos campos
                  </h3>
                  <dl className="vx-help__dl">
                    {bundle.hints.map((h) => (
                      <Fragment key={`${h.entity}.${h.field}`}>
                        <dt className="mono">{h.field}</dt>
                        <dd>
                          {h.short_tip}
                          {h.error_hint ? <span className="text-2"> {h.error_hint}</span> : null}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                </section>
              ) : null}

              {bundle.faq.length ? (
                <section aria-labelledby="vx-help-faq">
                  <h3 id="vx-help-faq" className="vx-help__section">
                    Perguntas frequentes
                  </h3>
                  {bundle.faq.map((f) => (
                    <details key={f.id} className="vx-help__faq">
                      <summary>{f.question}</summary>
                      <HelpMarkdown source={f.answer_md} />
                    </details>
                  ))}
                </section>
              ) : null}

              {bundle.glossary.length ? (
                <section aria-labelledby="vx-help-glossary">
                  <h3 id="vx-help-glossary" className="vx-help__section">
                    Glossário
                  </h3>
                  <dl className="vx-help__dl">
                    {bundle.glossary.map((g) => (
                      <Fragment key={g.term}>
                        <dt>{g.term}</dt>
                        <dd>{g.definition_md}</dd>
                      </Fragment>
                    ))}
                  </dl>
                </section>
              ) : null}
            </>
          ) : null}

          {onOpenCenter ? (
            <Button
              variant="link"
              onClick={() => {
                setOpen(false);
                onOpenCenter();
              }}
            >
              Abrir a central de ajuda
            </Button>
          ) : null}
        </div>
      </Dialog>
      {tour ? <HelpTourOverlay tour={tour} onClose={() => setTour(null)} /> : null}
    </>
  );
}
